import { ProductEntry, ProductEntryContainer } from '@features/base/private/components/product-entry/ProductEntry';
import { WidgetProps } from '@features/dashboard/types';
import { Close as CloseTabIcon, Add as NewTabIcon } from '@mui/icons-material';
import { Box, Divider, Tab, Tabs } from '@mui/material';
import { Product, ProductTenor } from '@protos/product';
import { Side } from '@protos/v2/order';
import { OtcExchange, OtcOrderType } from '@protos/v2/otcQuote';
import { streamV2RFQ } from '@services/context';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useEffect, useRef, useState } from 'react';
import Settings from '../settings/Settings';
import Widget, { Header } from '../Widget';
import { LadderQuantityEntry } from './LadderQuantityEntry';
import { LadderTab } from './LadderTab';
import { isLadderInfoEmpty, LadderInfo, LadderPayload, StoredLadderDef } from './LadderTypes';

import { useAppStore, useWidgetStore } from '../../../common/store/AppStore';
import { LadderStore } from './context/LadderStore';

export type LadderWidgetProps = WidgetProps & {
  payload?: LadderPayload;
};

// This widget is currently unused.

export const LadderWidgetContent = (props: LadderWidgetProps) => {
  const { payload, id, ...otherProps } = props;

  const { tabs, updateTab, addTab, deleteTab, tabIndex, setTabIndex, initFromPayload, refreshCurrentTab, orderAmount } = useWidgetStore<LadderStore>(
    props.id
  );

  const { products, tenors, adminApiClient } = useAppStore();

  const user = useUserContext();

  // This test payload is here for demo purposes since the actual payload interaction
  // was never implemented
  const testLadderDefs: StoredLadderDef[] = [
    { symbol: 'brt', tenorCode: 'g26', exchange: 'ice' },
    { symbol: 'brt', tenorCode: 'n26', exchange: 'ice' },
  ];

  const testPayload: Partial<LadderPayload> = { tabs: testLadderDefs };

  useEffect(() => {
    if (adminApiClient) {
      // TODO: assuming this ever gets used, remove the test payload
      const hasAnyTabs = initFromPayload({ ...testPayload, ...payload });
      setProductEntryOpen(!hasAnyTabs);
    }
  }, [payload, products, tenors, adminApiClient]);

  const currentTab = tabs.length > 0 ? tabs[tabIndex] : undefined;

  const needsData = isLadderInfoEmpty(currentTab);
  const [productEntryOpen, setProductEntryOpen] = useState(needsData);

  const onProductEntrySubmit = (
    product: Product,
    tenor: ProductTenor | null,
    front: ProductTenor | null,
    back: ProductTenor | null,
    unused: number,
    exchange: string | null
  ) => {
    updateTab(Number(tabIndex), { ...currentTab!, product, tenor, exchange });
    setProductEntryOpen(false);
  };

  const onProductEntryCancel = () => {
    setProductEntryOpen(false);
  };

  const onChangeTab = (event: React.SyntheticEvent, newValue: string | number) => {
    if (Number(newValue) === tabs.length) {
      addTab();
      return;
    }
    setTabIndex(Number(newValue));
  };

  const onTradeClick = (price: number, side: Side | undefined, info: LadderInfo, orderType: OtcOrderType) => {
    if (!info.product || !info.exchange || !info.tenor) return;
    streamV2RFQ.placeOrderQuote({
      symbol: info.product.symbol + info.tenor.code,
      amount: orderAmount.toString(),
      side: side === 'buy' ? Side.BUY : Side.SELL,
      price: price.toString(),
      exchange: info.exchange as OtcExchange,
      account_id: user?.abn_trad_accounts[0] || '',
      order_type: orderType,
    });
  };

  // TODO: replace this with the stream from the websocket when it's available
  const pollingCallbackId = useRef<number>(-1);
  useEffect(() => {
    const pollingCallback = () => {
      refreshCurrentTab();
      pollingCallbackId.current = window.setTimeout(pollingCallback, 1000);
    };

    pollingCallback();

    return () => {
      window.clearTimeout(pollingCallbackId.current);
    };
  }, []);

  if (!currentTab) {
    <Widget {...otherProps} id={id}>
      <Header title={otherProps.title}>
        <Settings id={`ladder-widget-${id}`} widgetId={id} />
      </Header>
    </Widget>;
  }

  return (
    <Widget {...otherProps} id={id}>
      <Header title={otherProps.title}>
        <Settings id={`ladder-widget-${id}`} widgetId={id} />
      </Header>

      <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
        <ProductEntryContainer isOpen={productEntryOpen || needsData}>
          <ProductEntry
            onSubmit={onProductEntrySubmit}
            onCancel={onProductEntryCancel}
            hideTargetPrice={true}
            hideExchange={false}
            hidePrices={true}
          />
        </ProductEntryContainer>

        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <Tabs value={currentTab?.index || 0} onChange={onChangeTab} sx={{ padding: 0 }}>
            {tabs.map((tp, index) => {
              const icon = (
                <CloseTabIcon
                  sx={{ transition: 'all 0.1s ease', opacity: 0.5, ':hover': { color: 'white', opacity: 1 } }}
                  fontSize="small"
                  onClick={e => {
                    e.stopPropagation();
                    deleteTab(index);
                  }}
                />
              );

              return (
                <Tab
                  id={tp.index.toString()}
                  label={tp.label}
                  icon={icon}
                  iconPosition={'end'}
                  key={tp.index.toString()}
                  sx={{ padding: '8px 4px' }}
                />
              );
            })}
            <Tab sx={{ ':hover': { color: 'white' } }} icon={<NewTabIcon sx={{ transition: 'all 0.1s ease' }} key={'addTab'} />} />
          </Tabs>

          {tabs.map(tp => {
            const hidden = tp.index !== tabIndex;
            const key = 'tab' + tp.index.toString();
            if (hidden) {
              return <div key={key}></div>;
            }

            return (
              <div role="tabpanel" hidden={hidden} key={key} id={tp.index.toString()} style={{ width: '100%', height: '100%', flexGrow: 1 }}>
                <LadderTab
                  key={key}
                  widgetId={props.id}
                  {...tp}
                  onLimitClick={(price, side) => {
                    onTradeClick(price, side, tp, OtcOrderType.limit);
                  }}
                  onFillOrKillClick={(price, side) => {
                    onTradeClick(price, side, tp, OtcOrderType.fill_or_kill);
                  }}
                />
              </div>
            );
          })}
          <Divider />
          <LadderQuantityEntry id={props.id} />
        </div>
      </Box>
    </Widget>
  );
};

export const LadderWidget = (props: LadderWidgetProps) => {
  // Guard for
  if (useAppStore().getWidgetData(props.id)) {
    return <LadderWidgetContent {...props} />;
  } else {
    return <></>;
  }
};
